import { groupBy } from 'es-toolkit';
import React, { useMemo } from 'react';

import { Level } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';

import { useAppSelector } from '../../../../components/hooks';
import { getVisibleLevelGuids, NULL_LEVEL_GUID_KEY } from '../../../../store/selectors/layoutDataSelectors';
import { getNullLevelDimensionElevation } from '../../../../store/selectors/layoutDocumentSelectors';
import { getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes } from '../../../../store/selectors/roofAndCeilingPlaneSelectors';
import { AutomaticRunDimensions } from '../AutomaticRunDimensions';
import { useTrussEnvelopesWithInsertionEnvelope } from '../useTrussEnvelopesWithInsertionEnvelope';

export function RoofPlaneEnvelopesRunDimensionsController() {
  const visibleLevelGuids = useAppSelector(getVisibleLevelGuids);

  const allLayoutModels = useAppSelector(getLayoutModelsWithPendingChanges);
  const nullLevelElevation = useAppSelector(getNullLevelDimensionElevation);
  const getElevation = (levelGuid: string | null) =>
    levelGuid == null ? nullLevelElevation : (allLayoutModels[levelGuid] as unknown as Level).plateElevation;

  const roofOutlines = useAppSelector(getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes);

  const trussEnvelopes = useTrussEnvelopesWithInsertionEnvelope();

  // memoized for stable envelope arrays for automatic run dimension caching
  const relevantTrussEnvelopes = useMemo(
    () =>
      groupBy(
        trussEnvelopes.filter(
          (envelope) => envelope.featurePatternContainerGuid == null && envelope.roofContainerGuid == null,
        ),
        (envelope) => envelope.levelGuid ?? NULL_LEVEL_GUID_KEY,
      ),
    [trussEnvelopes],
  );

  return Array.from(visibleLevelGuids).flatMap((levelGuid) => {
    const envelopes = relevantTrussEnvelopes[levelGuid ?? NULL_LEVEL_GUID_KEY];
    if (envelopes == null) return [];
    return roofOutlines[levelGuid ?? NULL_LEVEL_GUID_KEY].map((roofOutline, index) => (
      <AutomaticRunDimensions
        key={`${levelGuid}-${index}`}
        elevation={getElevation(levelGuid)}
        dimensionedArea={roofOutline}
        trussEnvelopes={envelopes}
      />
    ));
  });
}
